import { rpcClient } from "@/api/WebsocketClient"
import SWR, { Call } from "@/api/SWR"
import { reactive } from "@vue/reactivity"
import SortAndFilterUtil from "@/util/SortAndFilterUtil"
import Group from '@/model/directory/Group';
import Page from '@/model/Page';
import { groupStore } from '@/store/GroupStore';

export default class GeneratedGroupServiceApi {

    cache: Map<string, Call<any>> = new Map<string, Call<any>>()

    constructor() {
        this.init()
    }

    init() {
        window.setTimeout(() => {
            if (rpcClient) {
                rpcClient.apis.push(this)
            } else {
                this.init()
            }
        }, 1)
    }

    clearState() {
        this.cache = new Map<string, Call<any>>()
    }

    get connected(): boolean {
        return rpcClient.state.connected
    }

    _getGroupsForCurrentUser(): Promise<Group[]> {
        let rpcParams: any[] = Array.prototype.slice.call(arguments, 0, arguments.length).filter(arg => arg !== undefined)
        return rpcClient.call('getGroupsForCurrentUser', rpcParams, true).then((data: any) => {
            if (data && Array.isArray(data)) {
                const groups: Group[] = data.map(group => Object.assign(new Group(), group))
                groupStore.addOrReplaceGroups(groups)
                return groups
            } else return Promise.reject()
        })
    }


    getGroupsForCurrentUser(refresh?: boolean | number, sortBy?: string[] | string): SWR<Group[], string[]> {
        //@ts-ignore
        const result: SWR<Group[], string[]> = reactive(new SWR<Group[], string[]>())
        const args: any[] = Array.prototype.slice.call(arguments, 0, 1).filter(arg => arg !== undefined)
        const callId: string = '_getGroupsForCurrentUser' + JSON.stringify(args)
        const cached: Call<string[]> | undefined = this.cache.get(callId)
        if (refresh === undefined && cached && cached.ended) {
            for (const [ id, call ] of this.cache) {
                if (id.startsWith('_getGroupsForCurrentUser[') && id !== callId && (!call.ended || call.ended > cached.ended)) {
                    refresh = 3000
                    break
                }
            }
        }
        if (cached && !cached.ended) {
            result.call = cached
            cached.promise?.then((data: string[]) => {
                const groups: Group[] = []
                for (const id of data) {
                    const group: Group | undefined = groupStore.state.groups.get(id)
                    if (group) {
                        groups.push(group)
                    }
                }
                result.data = groups
            })
        } else if (refresh !== -1 && (!cached || refresh === true || (typeof refresh === 'number' && (cached.ended || 0) < (Date.now() - refresh)))) {
            const call = reactive(new Call<string[]>()) as Call<string[]>
            this.cache.set(callId, call)
            call.loading = !cached
            call.refreshing = !!cached
            //@ts-ignore since Array.filter does not provide nullsafe guard
            call.promise = this._getGroupsForCurrentUser().then((data: Group[]) => {
                //@ts-ignore since Array.filter does not provide nullsafe guard
                result.data = data
                //@ts-ignore since Array.filter does not provide nullsafe guard
                call.data = data.map(group => group.name || '')
                return call.data
            }).catch(e => {
                setTimeout(() => {
                  this.cache.delete(callId)
                }, 1000)
                return Promise.reject(e)
            }).finally(() => {
                call.ended = Date.now()
                call.loading = false
                call.refreshing = false
            })
            result.call = call
        }
        let groups: Group[] = [...groupStore.state.groups.values()]
        
        SortAndFilterUtil.sort(groups, sortBy)
        
        result.data = groups
        return result
    }

    getGroupsFilterByDeleted(deleted: string, sortBy?: string[] | string, pageIndex?: number, pageSize?: number, refresh?: boolean | number): SWR<Group[], string[]> {
        //@ts-ignore
        const result: SWR<Group[], string[]> = reactive(new SWR<Group[], string[]>())
        const args: any[] = Array.prototype.slice.call(arguments, 0, 4).filter(arg => arg !== undefined)
        const callId: string = '_getGroupsForCurrentUser' + JSON.stringify(args)
        const cached: Call<string[]> | undefined = this.cache.get(callId)
        if (refresh === undefined && cached && cached.ended) {
            for (const [ id, call ] of this.cache) {
                if (id.startsWith('_getGroupsForCurrentUser[') && id !== callId && (!call.ended || call.ended > cached.ended)) {
                    refresh = 3000
                    break
                }
            }
        }
        if (cached && !cached.ended) {
            result.call = cached
            cached.promise?.then((data: string[]) => {
                const groups: Group[] = []
                for (const id of data) {
                    const group: Group | undefined = groupStore.state.groups.get(id)
                    if (group) {
                        groups.push(group)
                    }
                }
                result.data = groups
            })
        } else if (refresh !== -1 && (!cached || refresh === true || (typeof refresh === 'number' && (cached.ended || 0) < (Date.now() - refresh)))) {
            const call = reactive(new Call<string[]>()) as Call<string[]>
            this.cache.set(callId, call)
            call.loading = !cached
            call.refreshing = !!cached
            //@ts-ignore since Array.filter does not provide nullsafe guard
            call.promise = this._getGroupsForCurrentUser().then((data: Group[]) => {
                //@ts-ignore since Array.filter does not provide nullsafe guard
                result.data = data
                //@ts-ignore since Array.filter does not provide nullsafe guard
                call.data = data.map(group => group.name || '')
                return call.data
            }).catch(e => {
                setTimeout(() => {
                  this.cache.delete(callId)
                }, 1000)
                return Promise.reject(e)
            }).finally(() => {
                call.ended = Date.now()
                call.loading = false
                call.refreshing = false
            })
            result.call = call
        }
        let groups: Group[] = [...groupStore.state.groups.values()]
        groups = SortAndFilterUtil.filter(groups, { deleted: deleted })
        SortAndFilterUtil.sort(groups, sortBy)
        if (pageSize !== null && pageSize !== undefined) {
            groups = groups.slice((pageIndex || 0) * pageSize, ((pageIndex || 0) + 1) * pageSize)
        }
        result.data = groups
        return result
    }

    getGroups(sortBy?: string[] | string, pageIndex?: number, pageSize?: number): Group[] {
        let groups: Group[] = [...groupStore.state.groups.values()]
        
        SortAndFilterUtil.sort(groups, sortBy)
        if (pageSize !== null && pageSize !== undefined) {
            groups = groups.slice((pageIndex || 0) * pageSize, ((pageIndex || 0) + 1) * pageSize)
        }
        return groups
    }

    getGroup(name: string): Group | undefined {
        return groupStore.state.groups.get(name)
    }
}
